import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import GeneralLegendNew from '@gp/components/GeneralLegendNew';
import ReportCard from '../../../../../components/ReportCard';
import CardHeader from './Header';
import CardBody from './Body';
import { CARD_TITLE } from '../../utils';
import { BoolFlag } from '@gp/enums/common';

import type { IBaseProps } from '@home/types/common';
import type { IRecord } from '@gp/types/global';
import type { IDPSReportHightlightVO } from '@gp/types/dpsReport/v2';

import './index.scss';

interface IPanelProps extends IBaseProps {
  startDate?: string;
  endDate?: string;
  data?: IDPSReportHightlightVO[];
}

interface IDPSReportHightlightVODateDataList {
  date: string;
  list: IDPSReportHightlightVO[];
}

const LEGEND_OPTIONS: IGeneralLegend[] = [
  {
    key: 'milestone',
    text: 'Milestone',
    dotColor: '#A6C9FF',
    isNotClickEvent: true,
  },
  {
    key: 'gr',
    text: 'Gate Review',
    dotColor: '#C0EBD2',
    isNotClickEvent: true,
  },
];

const formatDataByDateRange = (
  startDate?: string,
  endDate?: string,
  data?: IDPSReportHightlightVO[],
): IDPSReportHightlightVODateDataList[] => {
  const startDateM: moment.Moment | null = startDate ? moment(startDate) : null;
  const endDateM: moment.Moment | null = endDate ? moment(endDate) : null;
  if (!startDateM?.isValid() || !endDateM?.isValid()) return [];
  const result: IDPSReportHightlightVODateDataList[] = [];
  const dateM = moment(startDateM);
  while (dateM.isSameOrBefore(endDateM)) {
    const date = dateM.format('YYYY-MM-DD');
    const dateList = data?.filter((dataItem) => dataItem?.date === date);
    result.push({
      date,
      list: dateList || [],
    });
    dateM.add(1, 'days');
  }
  return result;
};

const filterList = (list: IDPSReportHightlightVO[], selectedLegend: IRecord<boolean>): IDPSReportHightlightVO[] => {
  if (!Array.isArray(list) || !list.length) return [];
  // REMARK: 注意这里图例默认打开，要展示出来数据
  const { milestone = true, gr = true } = selectedLegend || {};
  // milestone图例关闭，直接返回空数组
  if (!milestone) return [];
  // milestone、gr图例都打开，直接原样返回
  if (milestone && gr) return list;
  return list.filter((item) => {
    // 当前milestone是否为Gate Review
    const isGR = item?.is_gr === BoolFlag.True;
    // 筛选出非GR的milestone 或者 gr图例开启时为GR的milestone
    return !isGR || (isGR && gr);
  });
};

const Panel: React.FC<IPanelProps> = (props) => {
  const { style, className, startDate, endDate, data } = props;

  const dateDataList = useMemo<IDPSReportHightlightVODateDataList[]>(
    () => formatDataByDateRange(startDate, endDate, data),
    [startDate, endDate, data],
  );

  const [selectedLegend, setSelectedLegend] = useState<IRecord<boolean>>({ milestone: true, gr: true });

  return (
    <ReportCard
      title={CARD_TITLE}
      headerRight={
        <GeneralLegendNew
          className="dbg-dps-report-hightlights-panel-legend"
          options={LEGEND_OPTIONS}
          value={selectedLegend}
          onChange={setSelectedLegend}
        />
      }
    >
      <div style={style} className={classnames('dbg-dps-report-hightlights-panel', className)}>
        <ul className="dbg-dps-report-hightlights-panel__list">
          {dateDataList.map((dateDataItem) => {
            const { date, list } = dateDataItem || {};
            return (
              <li key={date} className="dbg-dps-report-hightlights-panel__item">
                <CardHeader date={date} num={list?.length} />
                <CardBody selectedLegend={selectedLegend} list={filterList(list, selectedLegend)} />
              </li>
            );
          })}
        </ul>
      </div>
    </ReportCard>
  );
};

export default Panel;
